import { Component } from 'react';

import Dropdown from '../../../components/ui/dropdown/dropdown';
import { IOC_TYPES } from '../../../context/ioc.types';
import { lazyInject } from '../../../context/module';
import { FileUploadRequestMetadata } from '../../../models/file-upload-request-metadata';
import { FormControl, FormGroup } from '../../../models/forms';
import { LightHttp } from '../../../services/light-http.service';
import { ProcessorResolverService } from '../../../services/processor-resolver.service';
import { BrowserUtils } from '../../../ui-utils/browser-utils';
import { StringUtils } from '../../../utils/string-utils';
import styles from './base64-decode.component.module.scss';
import sharedStyles from '../single-page.module.scss';
import { ProcessorMetadata } from '../../../types/types';
import { ProcessorMetadataUtils } from '../../../utils/processor-metadata.utils';
import { FullscreenHost } from '../fullscreen-host.component';

export class Base64DecodeComponent extends Component {
    fileUploadRequestMetadata: FileUploadRequestMetadata;
    processEndpoint;
    ready = false;
    availableEncodingList: {
        id: string;
        text: string;
        item: string;
        selected: boolean;
    }[];
    encoding = 'UTF-8';
    form = new FormGroup({
        textinput: new FormControl(''),
        reversetextinput: new FormControl(''),
    });

    @lazyInject(ProcessorResolverService) private resolverService: ProcessorResolverService;
    @lazyInject(LightHttp) private httpClinet: LightHttp;

    constructor(props) {
        super(props);
        if (BrowserUtils.hasWindow()) {
            this.resolverService.generateFileUploadRequestMetadataForGenericDataProcessing()
                .then(response => {
                    this.fileUploadRequestMetadata = response;
                    this.processEndpoint = ProcessorMetadataUtils.getProcessorEndpoint(this.fileUploadRequestMetadata.processorMetadataList[0]) + '/generic';
                    this.ready = true;
                });
        }
        this.availableEncodingList = this.getEncodingFormatSelectItems();
    }

    componentDidMount() {
        this.form.get('textinput').valueChanges.subscribe(
            () => {
                this.updateOutput();
            },
            (err) => console.error(err),
        );
    }

    updateOutput() {
        // Same processEndpoint race as the encoder — prefer browser-native
        // decode for UTF-8 (the default) so the page works the moment it
        // loads instead of waiting for the resolver promise.
        const raw = this.form.get('textinput').value || '';
        const decoded = this.decodeNative(raw);
        if (decoded !== null) {
            this.form.get('reversetextinput').setValue(decoded, { noEmit: true });
            this.forceUpdate();
            return;
        }
        if (!this.processEndpoint) {
            this.form.get('reversetextinput').setValue(
                `// Encoding ${this.encoding} requires server processing; please retry in a moment.`,
                { noEmit: true });
            this.forceUpdate();
            return;
        }
        const qdata = btoa(JSON.stringify({
            processType: 'decode',
            encoding: this.encoding,
        }));
        this.httpClinet.post<{ data: string }>(
            this.processEndpoint + '?qdata=' + encodeURIComponent(qdata),
            raw,
        ).subscribe({
            next: (response) => {
                this.form.get('reversetextinput').setValue(response.data, { noEmit: true });
                this.forceUpdate();
            },
        });
    }

    private decodeNative(b64: string): string | null {
        if (this.encoding !== 'UTF-8') return null;
        try {
            const clean = b64.replace(/\s+/g, '');
            if (!clean) return '';
            const binary = atob(clean);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
            return new TextDecoder('utf-8', { fatal: false }).decode(bytes);
        } catch {
            return '// Invalid base64 input';
        }
    }

    encodingSelected(value: any) {
        this.encoding = value.id;
        this.updateOutput();
    }

    getEncodingFormatSelectItems() {
        const selecteItems = StringUtils.supportedTextEncoding().map(item => {
            return {
                id: item[1],
                text: item[0],
                item: item[1],
                selected: false,
            };
        });
        selecteItems[0].selected = true;
        return selecteItems;
    }

    render() {
        return (<FullscreenHost><div className="row">
            <div className="col-12">
                <label className={sharedStyles.label}>Input</label>
                <textarea className={sharedStyles.textarea}
                    placeholder="Enter base64 string to decode..."
                    value={this.form.controls['textinput'].value}
                    onInput={(e: any) => this.form.controls['textinput'].setValue(e.target.value)} rows={6}></textarea>
            </div>

            <div className="col-12">
                <div className={sharedStyles.controls}>
                    <div className={sharedStyles.radioGroup}>
                        <label className={sharedStyles.label} style={{ marginBottom: 0, whiteSpace: "nowrap" }}>Source charset:</label>
                        <div style={{ minWidth: '200px' }}>
                            <Dropdown
                                values={this.availableEncodingList}
                                label={'Presets'}
                                valueFormatter={(v: any) => {
                                    return v && v.text;
                                }}
                                onSelect={e => this.encodingSelected(e)}
                                initialValue={this.availableEncodingList.find(v => v.selected)}
                                hasTooltip={(v) => false}
                                getTooltip={(v) => v.text}
                                shownFn={(v) => true}
                                emitInitialValueOnInit={true}
                            >
                            </Dropdown>
                        </div>
                    </div>
                </div>
            </div>

            <div className="col-12">
                <label className={sharedStyles.label}>Output</label>
                <textarea className={sharedStyles.textarea}
                    placeholder="Result will appear here..."
                    value={this.form.controls['reversetextinput'].value}
                    readOnly
                    rows={6}></textarea>
            </div>
        </div></FullscreenHost>);}
}
